import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, Eye, EyeOff, AlertTriangle, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForceChangePasswordPage() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const roleHome = () => {
    const r = user?.role;
    return r === 'admin' ? '/admin' : r === 'supervisor' ? '/supervisor' : r === 'client' ? '/client' : '/employee';
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 8) { setError('New password must be at least 8 characters.'); return; }
    if (newPassword !== confirmPassword) { setError('New passwords do not match.'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: currentPassword, new_password: newPassword });
      await refreshUser();
      toast.success('Password set successfully!');
      navigate(roleHome(), { replace: true });
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to set password';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-card relative overflow-hidden">
      <div className="absolute inset-0 grid-pattern opacity-25" aria-hidden />
      <Card className="w-full max-w-md relative z-10 bg-card/95 backdrop-blur-sm border-border rounded-sm">
        <CardHeader className="text-center space-y-3">
          <div className="mx-auto w-14 h-14 rounded-sm bg-primary/10 flex items-center justify-center">
            <ShieldAlert className="w-7 h-7 text-primary" strokeWidth={1.5} />
          </div>
          <CardTitle className="text-2xl font-barlow tracking-widest text-primary">SET YOUR PASSWORD</CardTitle>
          <CardDescription className="text-muted-foreground">
            You logged in with a temporary password. Please set a new password to continue.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Temporary Password</Label>
              <Input type={show ? 'text' : 'password'} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required data-testid="force-current-password" placeholder="The password from your email" className="rounded-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">New Password</Label>
              <div className="relative">
                <Input type={show ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required data-testid="force-new-password" className="rounded-sm pr-10" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Toggle password visibility">
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-[11px] text-muted-foreground">At least 8 characters.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Confirm New Password</Label>
              <Input type={show ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required data-testid="force-confirm-password" className="rounded-sm" />
            </div>
            {error && (
              <div className="flex items-center gap-2 text-red-500 text-sm bg-red-500/10 border border-red-500/30 p-3 rounded-sm" data-testid="force-error">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" /> <span>{error}</span>
              </div>
            )}
            <Button type="submit" disabled={loading} data-testid="force-save-btn" className="w-full bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm uppercase tracking-wider h-11">
              {loading ? 'Saving…' : 'Set Password & Continue'}
            </Button>
            <button type="button" onClick={logout} className="w-full text-sm text-muted-foreground hover:text-primary transition-colors flex items-center justify-center gap-2">
              <Lock className="w-4 h-4" /> Log out
            </button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
